import { useCallback } from "react";

// Configuración de imágenes desde settings
const Settings = require("../../../settings.json");
const imgLogoBase64 = Settings[process.env.NODE_ENV].logo_base64;
const imgQrBase64 = Settings[process.env.NODE_ENV].qr_code_base64;

// Constantes de configuración de empresa
const COMPANY_INFO = {
  name: "XXX",
  ruc: "XXX",
  address: "XXX",
  phones: "XXX",
  email: "XXX",
};

// Constantes de colores
const COLORS = {
  primary: "#1e40af",
  border: "#000000",
  headerBg: "#eeeeee",
};

/**
 * Configuración de formato según tipo de página
 * @param {string} formatType - Tipo de formato: "A4", "A5" o "TERMICA"
 * @returns {object} Configuración de página (tamaño, fuente, márgenes)
 */
const getPageConfig = (formatType) => {
  const configs = {
    A4: {
      pageSize: "A4",
      fontSize: 10,
      margins: [30, 30, 30, 30],
      logoWidth: 60,
    },
    A5: {
      pageSize: "A5",
      fontSize: 9,
      margins: [20, 20, 20, 20],
      logoWidth: 60,
    },
    TERMICA: {
      pageSize: { width: 226.77, height: "auto" },
      fontSize: 6,
      margins: [3, 3, 3, 3],
      logoWidth: 50,
    },
  };
  return configs[formatType] || configs.A4;
};

/**
 * Layout de tabla sin bordes horizontales internos
 * Solo muestra bordes externos (superior e inferior) y verticales completos
 */
const getNoInternalHorizontalBordersLayout = () => ({
  hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 1 : 0),
  vLineWidth: () => 1,
  hLineColor: () => COLORS.border,
  vLineColor: () => COLORS.border,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
});

/**
 * Layout de tabla sin bordes internos (ni horizontales ni verticales)
 * Solo muestra bordes externos
 */
const getNoInternalBordersLayout = () => ({
  hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 1 : 0),
  vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length ? 1 : 0),
  hLineColor: () => COLORS.border,
  vLineColor: () => COLORS.border,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
});

/**
 * Layout de tabla con borde después del header
 * Muestra bordes externos y una línea después de la primera fila (header)
 */
const getHeaderBorderLayout = () => ({
  hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 1 : 0),
  vLineWidth: () => 1,
  hLineColor: () => COLORS.border,
  vLineColor: () => COLORS.border,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
});

/**
 * Crea la tabla de información de la empresa
 * @param {number} fontSize - Tamaño de fuente
 * @param {string} formatType - Tipo de formato (A4, A5)
 */
const createCompanyInfoTable = (fontSize, formatType) => ({
  table: {
    widths: ["*"],
    body: [
      [{ 
        text: COMPANY_INFO.name, 
        bold: true, 
        color: COLORS.primary,
        fontSize: formatType === "A5" ? fontSize - 1 : fontSize, 
        alignment: "left", 
        margin: [8, 8, 8, 6] 
      }],
      [{ 
        text: `Dirección: ${COMPANY_INFO.address}`, 
        fontSize: formatType === "A5" ? fontSize - 2 : fontSize - 1, 
        alignment: "left",
        margin: [8, 3, 8, 3] 
      }],
      [{ 
        text: `Teléfonos: ${COMPANY_INFO.phones}`, 
        fontSize: formatType === "A5" ? fontSize - 2 : fontSize - 1, 
        alignment: "left",
        margin: [8, 3, 8, 3] 
      }],
      [{ 
        text: `Email: ${COMPANY_INFO.email}`, 
        fontSize: formatType === "A5" ? fontSize - 2 : fontSize - 1, 
        alignment: "left",
        margin: [8, 3, 8, 8] 
      }],
    ]
  },
  layout: getNoInternalHorizontalBordersLayout()
});

/**
 * Crea la tabla de RUC y número de comprobante
 * @param {object} data - Datos de la venta
 * @param {number} fontSize - Tamaño de fuente
 * @param {string} formatType - Tipo de formato (A4, A5)
 */
const createReceiptInfoTable = (data, fontSize, formatType) => ({
  table: {
    widths: ["*"],
    body: [
      [{ 
        text: `RUC: ${COMPANY_INFO.ruc}`, 
        bold: true, 
        fontSize: formatType === "A5" ? fontSize + 4 : fontSize + 5, 
        alignment: "center", 
        margin: [5, 8, 5, 4] 
      }],
      [{ 
        text: data.type_receipt_name, 
        bold: true, 
        fontSize: formatType === "A5" ? fontSize + 2 : fontSize + 3, 
        alignment: "center", 
        margin: [5, 4, 5, 4] 
      }],
      [{ 
        text: `No. ${data.number_sale}`, 
        bold: true, 
        fontSize: formatType === "A5" ? fontSize + 1 : fontSize + 2, 
        alignment: "center", 
        margin: [5, 4, 5, 8] 
      }],
      [{
        image: `data:image/png;base64,${imgQrBase64}`,
        width: formatType === "A5" ? 72 : 90,
        alignment: "center",
        margin: [0, 5, 0, 8]
      }]
    ]
  },
  layout: getNoInternalBordersLayout()
});

/**
 * Crea la tabla de datos del cliente
 * @param {object} data - Datos del cliente
 * @param {number} fontSize - Tamaño de fuente
 */
const createClientDataTable = (data, fontSize) => ({
  table: {
    widths: ["50%", "50%"],
    body: [
      [
        { text: `Nombres: ${data.client_name}`, fontSize: fontSize - 1, margin: [5, 1, 5, 1] },
        { text: `Identificación: ${data.client_document}`, fontSize: fontSize - 1, margin: [5, 1, 5, 1] }
      ],
      [
        { text: `Dirección: ${data.client_address || "RIOBAMBA"}`, fontSize: fontSize - 1, margin: [5, 1, 5, 1] },
        { text: `Teléfono: ${data.client_phone || "(000) ___-____"}`, fontSize: fontSize - 1, margin: [5, 1, 5, 1] }
      ],
      [
        { text: `Fecha: ${new Date(data.created_at).toLocaleDateString()}`, fontSize: fontSize - 1, margin: [5, 1, 5, 1] },
        { text: `Vendedor: ${data.user_name}`, fontSize: fontSize - 1, margin: [5, 1, 5, 1] }
      ]
    ]
  },
  layout: getNoInternalBordersLayout(),
  margin: [0, 0, 0, 10]
});

/**
 * Crea la tabla de productos
 * @param {object} data - Datos de la venta con detalles de productos
 * @param {number} fontSize - Tamaño de fuente
 */
const createProductsTable = (data, fontSize) => ({
  table: {
    headerRows: 1,
    widths: ["auto", "*", "auto", 60, 60],
    body: [
      // Header
      [
        { text: "Código", bold: true, fontSize, alignment: "left", fillColor: COLORS.headerBg, margin: [3, 5, 3, 5] },
        { text: "Descripción", bold: true, fontSize, alignment: "left", fillColor: COLORS.headerBg, margin: [3, 5, 3, 5] },
        { text: "Cant", bold: true, fontSize, alignment: "center", fillColor: COLORS.headerBg, margin: [3, 5, 3, 5] },
        { text: "Prec. Unit", bold: true, fontSize, alignment: "right", fillColor: COLORS.headerBg, margin: [3, 5, 3, 5], noWrap: true },
        { text: "Prec. Total", bold: true, fontSize, alignment: "right", fillColor: COLORS.headerBg, margin: [3, 5, 3, 5], noWrap: true },
      ],
      // Productos
      ...data.sales_details.map((item) => [
        { text: item.product_cf_code || "-", fontSize: fontSize - 1, alignment: "center", margin: [3, 2, 3, 2] },
        { text: item.product_name, fontSize: fontSize - 1, alignment: "left", margin: [3, 2, 3, 2] },
        { text: item.quantity.toString(), fontSize: fontSize - 1, alignment: "center", margin: [3, 2, 3, 2] },
        { text: item.unit_price.toFixed(2), fontSize: fontSize - 1, alignment: "right", margin: [3, 2, 3, 2] },
        { text: item.total_price.toFixed(2), fontSize: fontSize - 1, alignment: "right", margin: [3, 2, 3, 2] },
      ])
    ],
  },
  layout: getHeaderBorderLayout(),
  margin: [0, 0, 0, 10]
});

/**
 * Crea la tabla de datos adicionales (información de contacto)
 * @param {object} data - Datos del cliente
 * @param {number} fontSize - Tamaño de fuente
 */
const createAdditionalDataTable = (data, fontSize) => ({
  width: "55%",
  table: {
    widths: ["*"],
    body: [
      [{ text: "DATOS ADICIONALES", bold: true, fontSize, margin: [5, 2, 5, 2] }],
      [{ text: `Email: ${data.client_email || "consumidorfinal@centralcf.com"}`, fontSize: fontSize - 1, margin: [5, 2, 5, 2] }],
      [{ text: `Teléfono: ${data.client_phone || "00-000 ___-__ / (000) ___-____"}`, fontSize: fontSize - 1, margin: [5, 2, 5, 2] }],
      [{ text: `Forma de pago: Efectivo`, fontSize: fontSize - 1, margin: [5, 2, 5, 2] }],
    ]
  },
  layout: getNoInternalBordersLayout()
});

/**
 * Crea la tabla de totales (subtotales, IVA, total)
 * @param {object} data - Datos financieros de la venta
 * @param {number} fontSize - Tamaño de fuente
 */
const createTotalsTable = (data, fontSize) => ({
  width: "45%",
  table: {
    widths: ["*", "auto"],
    body: [
      [{ text: "Subtotal:", fontSize: fontSize - 1, margin: [5, 1, 3, 1] }, { text: data.subtotal_before_tax.toFixed(2), fontSize: fontSize - 1, alignment: "right", margin: [3, 1, 5, 1] }],
      [{ text: "Subtotal con descuento:", fontSize: fontSize - 1, margin: [5, 1, 3, 1] }, { text: "0.00", fontSize: fontSize - 1, alignment: "right", margin: [3, 1, 5, 1] }],
      [{ text: "Subtotal 15%:", fontSize: fontSize - 1, margin: [5, 1, 3, 1] }, { text: "0.00", fontSize: fontSize - 1, alignment: "right", margin: [3, 1, 5, 1] }],
      [{ text: "Subtotal sin impuestos:", fontSize: fontSize - 1, margin: [5, 1, 3, 1] }, { text: "0.00", fontSize: fontSize - 1, alignment: "right", margin: [3, 1, 5, 1] }],
      [{ text: "IVA 15%:", fontSize: fontSize - 1, margin: [5, 1, 3, 1] }, { text: data.tax_amount.toFixed(2), fontSize: fontSize - 1, alignment: "right", margin: [3, 1, 5, 1] }],
      [{ text: "TOTAL:", bold: true, fontSize, margin: [5, 1, 3, 1] }, { text: data.total_amount.toFixed(2), bold: true, fontSize, alignment: "right", margin: [3, 1, 5, 1] }],
    ]
  },
  layout: getNoInternalBordersLayout()
});

/**
 * Genera el contenido para formato térmico (recibo de 80mm)
 * @param {object} data - Datos de la venta
 * @param {object} config - Configuración de página
 */
const createThermalContent = (data, config) => {
  const { fontSize, logoWidth } = config;
  const separator = "------------------------------------------------------------------------------------------------------------------------------------";
  
  // Transformar productos para formato térmico (sin código)
  const thermalProducts = data.sales_details.map((item) => [
    { text: item.product_name, fontSize: 6, alignment: "left" },
    { text: item.quantity.toString(), alignment: "center", fontSize: 6 },
    { text: `$${item.unit_price.toFixed(2)}`, alignment: "right", fontSize: 6 },
    { text: `$${item.total_price.toFixed(2)}`, alignment: "right", fontSize: 6 },
  ]);

  return [
    // Logo
    { image: `data:image/png;base64,${imgLogoBase64}`, width: logoWidth, alignment: "center", margin: [0, 5, 0, 5] },
    
    // Información de la empresa
    { text: COMPANY_INFO.name, style: "title", alignment: "center" },
    { text: `RUC: ${COMPANY_INFO.ruc}`, style: "sub", alignment: "center" },
    { text: COMPANY_INFO.address, alignment: "center", fontSize },
    { text: `Tel: ${COMPANY_INFO.phones.split(" - ").slice(0, 2).join(" - ")}`, alignment: "center", margin: [0, 0, 0, 5], fontSize },
    { text: separator, alignment: "center", fontSize },
    
    // Información de la venta
    { text: `Factura No: ${data.number_sale}`, style: "bold", fontSize },
    { text: `Fecha: ${new Date(data.created_at).toLocaleDateString()}`, fontSize },
    { text: `Vendedor: ${data.user_name}`, margin: [0, 0, 0, 5], fontSize },
    { text: separator, alignment: "center", fontSize },
    
    // Cliente
    { text: `Cliente: ${data.client_name}`, fontSize },
    { text: `CI/RUC: ${data.client_document}`, margin: [0, 0, 0, 5], fontSize },
    { text: separator, alignment: "center", fontSize },
    
    // Tabla de productos
    {
      table: {
        headerRows: 1,
        widths: ["40%", "15%", "20%", "25%"],
        body: [
          [
            { text: "Descripción", bold: true, fontSize: 6 },
            { text: "Cant", bold: true, fontSize: 6, alignment: "center" },
            { text: "P.Unit", bold: true, fontSize: 6, alignment: "right" },
            { text: "Total", bold: true, fontSize: 6, alignment: "right" },
          ],
          ...thermalProducts,
        ],
      },
      layout: "lightHorizontalLines",
    },
    
    { text: separator, alignment: "center", fontSize },
    
    // Totales
    {
      table: {
        widths: ["*", "auto"],
        body: [
          [{ text: "Subtotal:", fontSize: 7 }, { text: `$${data.subtotal_before_tax.toFixed(2)}`, fontSize: 7, alignment: "right" }],
          [{ text: "IVA 15%:", fontSize: 7 }, { text: `$${data.tax_amount.toFixed(2)}`, fontSize: 7, alignment: "right" }],
          [{ text: "TOTAL:", bold: true, fontSize: 8 }, { text: `$${data.total_amount.toFixed(2)}`, bold: true, fontSize: 8, alignment: "right" }],
        ],
      },
      layout: "noBorders",
      margin: [0, 5, 0, 5],
    },
    
    { text: separator, alignment: "center", fontSize },
    
    // QR Code
    { image: `data:image/png;base64,${imgQrBase64}`, width: 40, alignment: "center", margin: [0, 5, 0, 5] },
    
    // Nota de garantía
    { 
      text: "Nota: En partes eléctricas no se aceptan cambios ni devoluciones. Para reclamo de garantía es importante traer informe técnico por escrito firmado o sellado y adjuntar un video como evidencia.", 
      alignment: "center", 
      fontSize: 6 
    },
  ];
};

/**
 * Genera el contenido para formatos A4/A5
 * @param {object} data - Datos de la venta
 * @param {object} config - Configuración de página
 * @param {string} formatType - Tipo de formato (A4 o A5)
 */
const createStandardContent = (data, config, formatType) => {
  const { fontSize, logoWidth } = config;

  return [
    // Header con dos columnas: empresa y comprobante
    {
      columns: [
        {
          width: "53%",
          stack: [
            { image: `data:image/png;base64,${imgLogoBase64}`, width: logoWidth, alignment: "center", margin: [0, 5, 0, 8] },
            createCompanyInfoTable(fontSize, formatType)
          ]
        },
        {
          width: "47%",
          stack: [createReceiptInfoTable(data, fontSize, formatType)]
        }
      ],
      columnGap: 15,
      margin: [0, 0, 0, 10]
    },

    // Datos del cliente
    createClientDataTable(data, fontSize),

    // Tabla de productos
    createProductsTable(data, fontSize),

    // Sección inferior: datos adicionales y totales
    {
      columns: [
        createAdditionalDataTable(data, fontSize),
        createTotalsTable(data, fontSize)
      ],
      columnGap: 10,
      margin: [0, 0, 0, 10]
    },

    // Nota de garantía
    {
      text: "Nota: En partes eléctricas no se aceptan cambios ni devoluciones. Para reclamo de garantía es importante traer informe técnico por escrito firmado o sellado y adjuntar un video como evidencia.",
      fontSize: fontSize - 2,
      alignment: "left",
      margin: [0, 0, 0, 0]
    }
  ];
};

/**
 * Hook personalizado para exportar documentos a PDF
 * Soporta formatos A4, A5 y térmico (80mm)
 */
export const usePdfExport = () => {
  const exportToPdf = useCallback(
    async (data, extraInfo = "A4", fileName = "factura") => {
      const [{ default: pdfMake }, pdfFontsModule] = await Promise.all([
        import("pdfmake/build/pdfmake"),
        import("pdfmake/build/vfs_fonts"),
      ]);
      const pdfFonts = pdfFontsModule.default || pdfFontsModule;
      if (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
        pdfMake.vfs = pdfFonts.pdfMake.vfs;
      } else if (pdfFonts.vfs) {
        pdfMake.vfs = pdfFonts.vfs;
      } else {
        pdfMake.vfs = pdfFonts;
      }

      const config = getPageConfig(extraInfo);
      const content = extraInfo === "TERMICA"
        ? createThermalContent(data, config)
        : createStandardContent(data, config, extraInfo);

      const docDefinition = {
        pageSize: config.pageSize,
        pageMargins: config.margins,
        content,
        styles: {
          title: { fontSize: config.fontSize + 2, bold: true },
          sub: { fontSize: config.fontSize, bold: true },
          bold: { bold: true },
        },
      };

      pdfMake.createPdf(docDefinition).open();
    },
    []
  );

  return { exportToPdf };
};
